import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import {
  Users,
  Shield,
  Activity,
  BarChart3,
  RefreshCw,
  Briefcase,
  FileText,
  ClipboardList,
  BrainCircuit,
  Key
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useLanguage } from '../../contexts/LanguageContext';
import { superAdminAPI } from '../../utils/api';

const SuperAdminDashboard = () => {
  const { t } = useLanguage();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchStats = useCallback(async () => {
    try {
      const response = await superAdminAPI.getDashboard();
      setStats(response.data);
    } catch (error) {
      console.error('Error loading super admin dashboard:', error);
      toast.error(t('superadmin.errors.loadStats', 'Error loading statistics'));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [t]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const usersStats = stats?.users || {};
  const modulesStats = stats?.modules || {};
  const recentUsers = stats?.recentUsers || [];
  const topActiveUsers = stats?.topActiveUsers || [];

  const activeRate = useMemo(() => {
    if (!usersStats.totalUsers) return 0;
    return Math.round(((usersStats.activeUsers || 0) / usersStats.totalUsers) * 100);
  }, [usersStats.activeUsers, usersStats.totalUsers]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchStats();
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-[70vh]">
          <div className="flex items-center gap-3 text-gray-500">
            <RefreshCw className="w-5 h-5 animate-spin" />
            <span>{t('common.loading', 'Loading...')}</span>
          </div>
        </div>
      </div>
    );
  }

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString();
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
        <div className="flex flex-col gap-2">
          <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
            <Shield className="w-6 h-6 text-indigo-600" />
            {t('superadmin.dashboard.title', 'Super Admin Dashboard')}
          </h1>
          <p className="text-sm text-gray-600">
            {t('superadmin.dashboard.subtitle', 'Platform global control panel')}
          </p>
        </div>
        <button
          type="button"
          onClick={handleRefresh}
          disabled={refreshing}
          className="text-xs px-3 py-2 rounded-lg border border-gray-200 text-gray-600 hover:border-indigo-200 inline-flex items-center gap-1.5 self-start"
        >
          <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
          {t('common.refresh', 'Refresh')}
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="text-xs text-gray-500">{t('superadmin.stats.totalUsers', 'Total users')}</div>
          <div className="text-2xl font-semibold text-gray-900 mt-1">{usersStats.totalUsers || 0}</div>
          <div className="text-xs text-gray-500 mt-2">
            {(usersStats.totalAdmins || 0)} {t('superadmin.stats.admins', 'admins')} • {(usersStats.totalRH || 0)} {t('superadmin.stats.rh', 'RH')}
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="text-xs text-gray-500">{t('superadmin.stats.activeUsers', 'Active users')}</div>
          <div className="text-2xl font-semibold text-emerald-600 mt-1">{usersStats.activeUsers || 0}</div>
          <div className="text-xs text-gray-500 mt-2">{activeRate}% {t('superadmin.stats.activeRate', 'active rate')}</div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="text-xs text-gray-500">{t('superadmin.dashboard.newUsers7d', 'New users (7 days)')}</div>
          <div className="text-2xl font-semibold text-gray-900 mt-1">{usersStats.newLast7Days || 0}</div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="text-xs text-gray-500">{t('superadmin.dashboard.envConfigured', 'Configured variables')}</div>
          <div className="text-2xl font-semibold text-indigo-600 mt-1">
            {modulesStats.envConfigured || 0}/{modulesStats.envVariables || 0}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <h2 className="text-sm font-semibold text-gray-900 mb-3">{t('superadmin.dashboard.modulesTitle', 'Platform modules')}</h2>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <div className="p-3 rounded-lg bg-gray-50 border border-gray-100">
            <div className="inline-flex items-center gap-2 text-xs text-gray-500"><Briefcase className="w-3.5 h-3.5" />{t('dashboard.nav.offers', 'Job offers')}</div>
            <div className="text-lg font-semibold text-gray-900 mt-1">{modulesStats.offers || 0}</div>
          </div>
          <div className="p-3 rounded-lg bg-gray-50 border border-gray-100">
            <div className="inline-flex items-center gap-2 text-xs text-gray-500"><FileText className="w-3.5 h-3.5" />{t('dashboard.nav.cvs', 'CV Library')}</div>
            <div className="text-lg font-semibold text-gray-900 mt-1">{modulesStats.cvs || 0}</div>
          </div>
          <div className="p-3 rounded-lg bg-gray-50 border border-gray-100">
            <div className="inline-flex items-center gap-2 text-xs text-gray-500"><ClipboardList className="w-3.5 h-3.5" />{t('dashboard.nav.applications', 'Applications')}</div>
            <div className="text-lg font-semibold text-gray-900 mt-1">{modulesStats.applications || 0}</div>
          </div>
          <div className="p-3 rounded-lg bg-gray-50 border border-gray-100">
            <div className="inline-flex items-center gap-2 text-xs text-gray-500"><BrainCircuit className="w-3.5 h-3.5" />{t('dashboard.nav.results', 'Results')}</div>
            <div className="text-lg font-semibold text-gray-900 mt-1">{modulesStats.results || 0}</div>
          </div>
          <div className="p-3 rounded-lg bg-gray-50 border border-gray-100">
            <div className="inline-flex items-center gap-2 text-xs text-gray-500"><Key className="w-3.5 h-3.5" />{t('dashboard.nav.env', 'Variables')}</div>
            <div className="text-lg font-semibold text-gray-900 mt-1">{modulesStats.envVariables || 0}</div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <h2 className="text-sm font-semibold text-gray-900 inline-flex items-center gap-2">
            <Users className="w-4 h-4 text-indigo-600" />
            {t('superadmin.activity.recentUsers', 'New users (last 7 days)')}
          </h2>
          <div className="mt-3 space-y-2">
            {recentUsers.length === 0 ? (
              <div className="text-sm text-gray-500 py-4 text-center">
                {t('superadmin.activity.noRecentUsers', 'No recent users')}
              </div>
            ) : (
              recentUsers.map((user) => (
                <div key={user.id} className="p-3 border border-gray-100 rounded-lg flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-gray-900 truncate">{user.name || user.email}</div>
                    <div className="text-xs text-gray-500 mt-0.5">{user.email}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs font-medium text-gray-600">{user.role}</div>
                    <div className="text-xs text-gray-500 mt-0.5">{formatDate(user.created_at)}</div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <h2 className="text-sm font-semibold text-gray-900 inline-flex items-center gap-2">
            <Activity className="w-4 h-4 text-indigo-600" />
            {t('superadmin.activity.topUsers', 'Top active users')}
          </h2>
          <div className="mt-3 space-y-2">
            {topActiveUsers.length === 0 ? (
              <div className="text-sm text-gray-500 py-4 text-center">
                {t('superadmin.activity.noActiveUsers', 'No recent activity')}
              </div>
            ) : (
              topActiveUsers.map((user) => (
                <div key={user.id} className="p-3 border border-gray-100 rounded-lg flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-gray-900 truncate">{user.name || user.email}</div>
                    <div className="text-xs text-gray-500 mt-0.5">{user.email}</div>
                  </div>
                  <span className="text-xs text-indigo-700 font-semibold bg-indigo-50 px-2 py-1 rounded-md">
                    {user.analyses_count || 0} {t('superadmin.dashboard.analyses', 'analyses')}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <motion.div whileHover={{ y: -2 }} transition={{ duration: 0.2 }}>
          <Link
            to="/dashboard/super-admin/users"
            className="block bg-white rounded-lg border border-gray-200 p-4 hover:border-indigo-200 transition-colors"
          >
            <div className="inline-flex items-center justify-center h-10 w-10 rounded-lg bg-indigo-50 text-indigo-600">
              <Users className="w-5 h-5" />
            </div>
            <h3 className="text-sm font-semibold text-gray-900 mt-3">
              {t('superadmin.actions.userManagement', 'User management')}
            </h3>
            <p className="text-xs text-gray-500 mt-1">
              {t('superadmin.actions.userDescription', 'Manage admins and HR users')}
            </p>
          </Link>
        </motion.div>

        <motion.div whileHover={{ y: -2 }} transition={{ duration: 0.2 }}>
          <Link
            to="/dashboard/super-admin/env"
            className="block bg-white rounded-lg border border-gray-200 p-4 hover:border-indigo-200 transition-colors"
          >
            <div className="inline-flex items-center justify-center h-10 w-10 rounded-lg bg-indigo-50 text-indigo-600">
              <Key className="w-5 h-5" />
            </div>
            <h3 className="text-sm font-semibold text-gray-900 mt-3">
              {t('superadmin.actions.variables', 'Environment variables')}
            </h3>
            <p className="text-xs text-gray-500 mt-1">
              {t('superadmin.actions.variablesDescription', 'Configure critical runtime keys')}
            </p>
          </Link>
        </motion.div>

        <motion.div whileHover={{ y: -2 }} transition={{ duration: 0.2 }}>
          <Link
            to="/dashboard/analytics"
            className="block bg-white rounded-lg border border-gray-200 p-4 hover:border-indigo-200 transition-colors"
          >
            <div className="inline-flex items-center justify-center h-10 w-10 rounded-lg bg-indigo-50 text-indigo-600">
              <BarChart3 className="w-5 h-5" />
            </div>
            <h3 className="text-sm font-semibold text-gray-900 mt-3">
              {t('superadmin.actions.analytics', 'Analytics')}
            </h3>
            <p className="text-xs text-gray-500 mt-1">
              {t('superadmin.actions.analyticsDescription', 'Detailed platform statistics')}
            </p>
          </Link>
        </motion.div>
      </div>
    </div>
  );
};

export default SuperAdminDashboard;
